import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { Box, MenuItem, Select, Slider, Stack, Typography } from "@mui/material";

import { IAssetAllocation, IProfileAssetAllocation } from "store/types/user/social";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { IFormikValues } from "../EditWindow";
import CloseIcon from "@mui/icons-material/Close";

interface IAssetAllocationComponentProps {
  item: IProfileAssetAllocation;
  arrayHelpers: any;
  index: number;
  availableAllocations: IAssetAllocation[];
}

export const AssetComponent: React.FC<IAssetAllocationComponentProps> = ({
  item,
  arrayHelpers,
  index,
  availableAllocations
}) => {
  const { values } = useFormikContext<IFormikValues>();
  const [sliderValue, setSliderValue] = useState<number>(item.direct || 50);
  const [assetValue, setAssetValue] = useState<number>(item.percentage || 1);
  const [allPercentFieldsWithoutCurrent, setAllPercentFieldsWithoutCurrent] = useState<number>(0);
  const [showDeleteButton, setShowDeleteButton] = useState<boolean>(false);
  const currentAsset = availableAllocations.find((asset) => asset.id === item.id);

  useEffect(() => {
    // calculated percentage without current value
    const validAssets = values.asset_allocations.filter((item: IProfileAssetAllocation) => item.title !== "");
    setAllPercentFieldsWithoutCurrent(
      validAssets.reduce((acc: number, item: IProfileAssetAllocation) => acc + item.percentage, 0) - item.percentage
    );
  }, [assetValue]);

  const directValue = sliderValue;
  const fundsValue = 100 - sliderValue;

  useEffect(() => {
    arrayHelpers.replace(index, { ...item, direct: directValue, funds: fundsValue });
  }, [directValue, fundsValue]);

  return (
    <Box
      onMouseEnter={() => {
        if (showDeleteButton) return;
        setShowDeleteButton(true);
      }}
      onMouseLeave={() => {
        if (!showDeleteButton) return;
        setShowDeleteButton(false);
      }}
    >
      <Stack direction={"row"} sx={{ gap: "20px", marginBottom: "11px" }}>
        <Box>
          <Typography sx={{ marginBottom: "20px", color: "#5d7782", fontSize: "16px" }}>Type</Typography>
          <Select
            sx={{ width: "260px", borderRadius: "8px", height: "36.3px" }}
            value={item.title || ""}
            onChange={(e) => {
              const currentOptionId = availableAllocations?.find(
                (option: IAssetAllocation) => option.title === e.target.value
              )?.id;
              arrayHelpers.replace(index, { ...item, id: currentOptionId, title: e.target.value });
            }}
          >
            {availableAllocations.map((item: IAssetAllocation, index) => {
              return (
                <MenuItem
                  sx={{
                    ...(values.asset_allocations.some(
                      (selectedAllocation: IProfileAssetAllocation) => selectedAllocation.title === item.title
                    ) && {
                      display: "none"
                    })
                  }}
                  key={index}
                  value={item.title}
                >
                  {item.title}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ marginBottom: "20px", color: "#5d7782", fontSize: "16px" }}>Percentage</Typography>
          <NumberInput
            disabled={!item.title}
            sx={{
              width: "102px",
              ".MuiInputBase-input": {
                padding: "8px 14px"
              }
            }}
            defaultValue={assetValue}
            // error={!!errors?.value}
            // helperText={errors?.value}
            numberInputProps={{
              suffix: "%",
              allowNegative: false,
              isNumericString: true
            }}
            size="small"
            value={item.percentage}
            onChange={(e) => {
              if (allPercentFieldsWithoutCurrent + Number(e.target.value) > 100) {
                setAssetValue(Number(100 - allPercentFieldsWithoutCurrent));
                arrayHelpers.replace(index, { ...item, percentage: Number(100 - allPercentFieldsWithoutCurrent) });
                return;
              }
              setAssetValue(Number(e.target.value));
              arrayHelpers.replace(index, { ...item, percentage: Number(e.target.value) });
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", marginBottom: "13px" }}>
          {showDeleteButton && (
            <CloseIcon
              sx={{ cursor: "pointer", fontSize: "20px" }}
              onClick={() => {
                arrayHelpers.remove(index);
              }}
            />
          )}
        </Box>
      </Stack>
      <Typography sx={{ color: "#5d7782", fontSize: "13px" }}>Investment</Typography>
      {currentAsset?.splittable && (
        <Box sx={{ marginBottom: "20px" }}>
          <Slider
            sx={{
              width: "245px",
              margin: "0px 5px",
              ".MuiSlider-mark": {
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                opacity: 1,
                bgcolor: "#bac7ce"
              },
              ".MuiSlider-rail": {
                bgcolor: "#bac7ce",
                opacity: 1
              },
              ".MuiSlider-track": {
                bgcolor: "#bac7ce",
                opacity: 1,
                border: "none"
              },
              "& .MuiSlider-thumb": {
                "&:after": {
                  bgcolor: "#ffffff",
                  border: "1px solid #0a3142",
                  width: "18px",
                  height: "18px"
                }
              }
            }}
            value={sliderValue}
            onChange={(event, value) => {
              setSliderValue(value as number);
            }}
            marks
            valueLabelDisplay="auto"
            step={10}
            min={0}
            max={100}
          />
          <Box
            sx={{ color: "#0a3142" }}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            width="260px"
          >
            <Typography fontSize="13px" lineHeight={1.05}>
              Direct<p>{directValue}%</p>
            </Typography>
            <Typography fontSize="13px" lineHeight={1.05}>
              Funds<p style={{ textAlign: "end" }}>{fundsValue}%</p>
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
